// Imports ------------------------------------------------------------------->

import { Service, service, inject } from './di'
import {
    LoyaltyCard,
    LoyaltyProgram,
    LoyaltyProgramMerchant,
} from '../definitions/CommonDefinitions'
import { LoyaltyProgramJSON } from '../definitions/ApiDefinitions'
import { ILoyaltyCardProvider } from './definitions/ILoyaltyCardProvider'
import { AuthUser } from './definitions/IAuthenticationService'
import { DefaultCardProvider } from './cards/DefaultCardProvider'
import { AuthenticationService } from './AuthenticationService'
import { LocationService } from './LocationService'
import { ConfigService, RuntimeConfig } from './ConfigService'
import * as array from '../utils/array'
import { ApiService } from './ApiService'

// Private Definitions ------------------------------------------------------->

function liftLoyaltyProgram(raw: any): LoyaltyProgram {
    const config = ConfigService.getInstance()
    if (raw.id === 46 && config.RUNTIME === RuntimeConfig.PG) {
        // Hack for Meijer. In P&G env, we need to use "phoneandpin" instead
        // of the default ("phone")
        // FW-1371: Meijer change for PG Everyday
        raw.validation_method = 'phoneandpin'
    }

    return {
        id: raw.id,
        name: raw.name,
        isLoadToCard: raw.is_load_to_card,
        isRebate: raw.is_rebate,
        validationMethod: raw.validation_method,
        redemptionMethod: raw.redemption_method,
        phoneRegex: raw.phone_regex,
        pinRegex: raw.pin_regex,
        cardRegex: raw.card_regex,
        cardImageUrl: fixUrl_(raw.card_image_url),
        largeLogoImageUrl: fixUrl_(raw.large_logo_image_url),
        mediumLogoImageUrl: fixUrl_(raw.medium_logo_image_url),
        smallLogoImageUrl: fixUrl_(raw.small_logo_image_url),
        logoBackingColour: raw.logo_backing_colour,
    }
}

function liftLoyaltyProgramMerchants(raw: any): LoyaltyProgramMerchant {
    return {
        id: raw.id,
        loyaltyProgramId: raw.loyalty_program_id,
        merchantId: raw.merchant_id,
    }
}

function fixUrl_(url: string) {
    /* Backflipp/accounts API returns image URLs that start with 'http'. This
     * triggers 'unsafe content' warnings in Chrone and is extremely annoying.
     * Fix all the URLs to use 'https' instead */
    if (/^http:\/\//.test(url)) {
        return `https://${url.substr('http://'.length, url.length)}`
    }
    return url
}

function liftLoyaltyCard(raw: any): LoyaltyCard {
    return {
        cardId: raw.card_id,
        commitVersion: raw.commit_version,
        createdAt: raw.created_at ? new Date(raw.created_at) : null,
        id: raw.id,
        loyaltyProgramId: raw.loyalty_program_id,
        name: raw.name,
        phoneNumber: raw.phone_number,
        updatedAt: raw.updated_at ? new Date(raw.updated_at) : null,
        registered: raw.registered,
        token: raw.token ? raw.token : null,
    }
}

// Public Definitions -------------------------------------------------------->

@service
export class LoyaltyProgramService extends Service {
    @inject auth: AuthenticationService
    @inject locationService: LocationService
    @inject api: ApiService
    @inject config: ConfigService

    private _loyaltyProgramsJSON: Promise<LoyaltyProgramJSON>
    private _cardProvider: ILoyaltyCardProvider

    constructor() {
        super()
        /* Loyalty card are stored per-user. Listen for auth event so we can
         * clear cache when user logs out */
        window.addEventListener('flipp-auth', this._handleAuthEvent)
        this._cardProvider = new DefaultCardProvider()
    }

    getLoyaltyCards(user: AuthUser) {
        return this._cardProvider.getLoyaltyCards(user).then((cards) => {
            if (this.config.RUNTIME === RuntimeConfig.PG) {
                // HACK: P&G experience uses the postcard /getLoyaltyCards API which does not contain all
                // the card info (eg - no name). Fill it in
                return this.getLoyaltyPrograms().then((programs) => {
                    cards.loyalty_cards.forEach((card) => {
                        const program = array.find(
                            programs,
                            (program) => program.id === card.loyalty_program_id
                        )
                        if (program) card.name = program.name
                    })
                    return cards.loyalty_cards.map(liftLoyaltyCard)
                })
            }
            return cards.loyalty_cards.map(liftLoyaltyCard)
        })
    }

    getLoyaltyCard(id: string, user: AuthUser) {
        return this._cardProvider.getLoyaltyCards(user).then((cards) => {
            const card = array.find(
                cards.loyalty_cards,
                (card) => card.id === id
            )
            if (this.config.RUNTIME === RuntimeConfig.PG) {
                // HACK: P&G experience uses the postcard /getLoyaltyCards API which does not contain all
                // the card info (eg - no name). Fill it in
                return this.getLoyaltyPrograms().then((programs) => {
                    const program = array.find(
                        programs,
                        (program) => program.id === card.loyalty_program_id
                    )
                    if (program) card.name = program.name
                    return liftLoyaltyCard(card)
                })
            }
            return card ? liftLoyaltyCard(card) : null
        })
    }

    getLoyaltyPrograms() {
        return this._getLoyaltyPrograms(this.locationService.postalCode).then(
            (programs) => {
                return programs.loyalty_programs
                    .map((program) => liftLoyaltyProgram(program))
                    .sort((p1, p2) => p1.name.localeCompare(p2.name))
            }
        )
    }

    getLoyaltyProgram(id: number) {
        return this._getLoyaltyPrograms(this.locationService.postalCode).then(
            (programs) => {
                const program = array.find(
                    programs.loyalty_programs,
                    (program) => program.id === id
                )
                return program ? liftLoyaltyProgram(program) : null
            }
        )
    }

    getLoyaltyProgramMerchants() {
        return this._getLoyaltyPrograms(this.locationService.postalCode).then(
            (programs) => {
                return programs.loyalty_program_merchants.map((program) =>
                    liftLoyaltyProgramMerchants(program)
                )
            }
        )
    }

    clearLoyaltyCards() {
        this._cardProvider.clear()
    }

    private _getLoyaltyPrograms = (postalCode: string) => {
        if (!this._loyaltyProgramsJSON) {
            this._loyaltyProgramsJSON = this.api.getLoyaltyPrograms(postalCode)
        }
        return this._loyaltyProgramsJSON
    }

    private _handleAuthEvent = () => {
        this.clearLoyaltyCards()
    }
}
